import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';

// Makes an ngx-bootstrap modal resizable by dragging its bottom-right corner.
//
// Put it on the root element of the modal's template (the one that also carries cdkDrag with
// cdkDragRootElement=".modal-content") and open the modal with class 'resizable-favorite-dialog'.
// The browser's own `resize: both` does the dragging, set on .modal-content by the
// `kig-resizable-modal` class (see styles.scss), so the header drag-to-move keeps working.
//
// The directive only sets the starting size and anchors the dialog:
//  - the width comes from [kigResizableModal] (px), never wider than the window;
//  - the height comes from [resizableHeight] (px), or stays automatic until the user drags;
//  - the dialog is anchored at its top-left (not centered by a translate), so the corner the
//    user drags follows the mouse exactly instead of moving at half speed.
@Directive({
  standalone: false,
  selector: '[kigResizableModal]'
})
export class ResizableModalDirective implements AfterViewInit, OnDestroy {
  // Starting width in pixels.
  @Input('kigResizableModal') width = 1100;
  // Starting height in pixels; leave unset for automatic height.
  @Input() resizableHeight: number | null = null;

  private modalContent: HTMLElement | null = null;
  private modalDialog: HTMLElement | null = null;

  constructor(private host: ElementRef<HTMLElement>) {}


  ngAfterViewInit(): void {
    const content = this.host.nativeElement.closest('.modal-content') as HTMLElement | null;
    const dialog = this.host.nativeElement.closest('.modal-dialog') as HTMLElement | null;
    if (content == undefined || dialog == undefined) {
      return;
    }
    this.modalContent = content;
    this.modalDialog = dialog;

    // The window can be smaller than the default size (and small windows should still show
    // the whole modal), so leave a margin.
    const maxWidth = Math.floor(window.innerWidth * 0.96);
    const width = Math.min(this.width, maxWidth);
    const height = this.resizableHeight != undefined
      ? Math.min(this.resizableHeight, Math.floor(window.innerHeight * 0.94))
      : null;

    content.classList.add('kig-resizable-modal');
    this.host.nativeElement.classList.add('kig-resizable-modal-root');

    content.style.width = width + 'px';
    if (height != undefined) {
      content.style.height = height + 'px';
    }

    // Anchor the dialog at its top-left, starting centered horizontally.
    dialog.classList.add('kig-resizable-dialog');
    dialog.style.left = Math.max(8, Math.floor((window.innerWidth - width) / 2)) + 'px';
  }


  ngOnDestroy(): void {
    // The modal is being closed and its DOM removed; nothing is kept or saved, so each
    // opening starts at the default size.
    this.modalContent = null;
    this.modalDialog = null;
  }
}
